import {
  differenceInMinutes,
  differenceInSeconds,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  setHours,
  startOfDay,
} from 'date-fns';
import {MeetingAttendee} from '../../data';

export type MeetingLike = {
  id: string;
  startDateTime: string;
  endDateTime: string;
  attendees: MeetingAttendee[];
  onlineMeetingUrl?: string;
};

export type SelectionOptions = {
  allDayUntilHour: number;
  alwaysShowIfStartsWithinMinutes: number;
  hideCurrentIfStartedMoreThanMinutesAgo: number;
  excludeMeetingsOnDifferentDays: boolean;
};

export const defaultSelectionOptions: SelectionOptions = {
  allDayUntilHour: 10,
  alwaysShowIfStartsWithinMinutes: Infinity,
  hideCurrentIfStartedMoreThanMinutesAgo: 12 * 60,
  excludeMeetingsOnDifferentDays: false,
};

export const selectMeetingsToShow = <TMeeting extends MeetingLike>(
  meetings: TMeeting[],
  time: Date,
  options: SelectionOptions,
): TMeeting[] => {
  // Initialize some helper variables
  const freshAge = options.alwaysShowIfStartsWithinMinutes;
  const outdatedAge = options.hideCurrentIfStartedMoreThanMinutesAgo;
  const hideDifferentDayMeetings = options.excludeMeetingsOnDifferentDays;
  const hideAllDayMeetings = !isBefore(time, setHours(startOfDay(time), options.allDayUntilHour));

  // Note: duplicate iterations won't matter as array size is small
  meetings = dedupeMeetings(meetings)
    // Take out meetings we never want to show
    .filter((m) => meetingIsWithOtherPeople(m))
    .filter((m) => !meetingStartedLongAgo(m, time, outdatedAge))
    .filter((m) => !(hideDifferentDayMeetings && !meetingStartsOnDay(m, time)))
    // Ensure correct order
    .sort(compareByStartTimeEndTime);

  // Select the right ranges of meetings to show
  const {ongoing, aboutToStart, future} = groupByTiming(meetings, time, freshAge);

  const relevantNow = ongoing.concat(aboutToStart).filter((m) => !(hideAllDayMeetings && meetingSpansOneOrMoreDays(m)));

  if (relevantNow.length === 0) {
    return takeFirstThatShareStartTime(future);
  }

  return relevantNow;
};

// Utils
function meetingIsWithOtherPeople(meeting: MeetingLike): boolean {
  return meeting.attendees.length > 1 || meeting.onlineMeetingUrl !== undefined;
}

function meetingSpansOneOrMoreDays(meeting: MeetingLike): boolean {
  const durationMinutes = differenceInMinutes(new Date(meeting.endDateTime), new Date(meeting.startDateTime));

  return durationMinutes >= 24 * 60 - 1; // Note: count a meeting from 00:00 to 23:59 as a full day event
}

function meetingIsOver<TMeeting extends MeetingLike>(meeting: TMeeting, time: Date): boolean {
  return isAfter(time, new Date(meeting.endDateTime));
}

function meetingIsOngoing<TMeeting extends MeetingLike>(meeting: TMeeting, time: Date): boolean {
  return isWithinInterval(time, {
    start: new Date(meeting.startDateTime),
    end: new Date(meeting.endDateTime),
  });
}

function compareByStartTimeEndTime<TMeeting extends MeetingLike>(m1: TMeeting, m2: TMeeting): number {
  return m1.startDateTime.localeCompare(m2.startDateTime) || m1.endDateTime.localeCompare(m2.endDateTime);
}

function meetingIsAboutToStart<TMeeting extends MeetingLike>(
  meeting: TMeeting,
  time: Date,
  minuteThreshold: number,
): boolean {
  const secondsUntilStart = differenceInSeconds(new Date(meeting.startDateTime), time);
  return secondsUntilStart > 0 && secondsUntilStart <= minuteThreshold * 60;
}

function meetingStartedLongAgo<TMeeting extends MeetingLike>(
  meeting: TMeeting,
  time: Date,
  minuteThreshold: number,
): boolean {
  const secondsSinceStart = differenceInSeconds(time, new Date(meeting.startDateTime));
  return secondsSinceStart > minuteThreshold * 60;
}

function meetingStartsOnDay<TMeeting extends MeetingLike>(meeting: TMeeting, time: Date): boolean {
  return isSameDay(time, new Date(meeting.startDateTime));
}

function dedupeMeetings<TMeeting extends MeetingLike>(meetings: Array<TMeeting>): Array<TMeeting> {
  const uniqueById = new Map(meetings.map((m) => [m.id, m]));
  return Array.from(uniqueById.values());
}

function groupByTiming<TMeeting extends MeetingLike>(
  meetings: Array<TMeeting>,
  time: Date,
  aboutToStartMinuteThreshold: number,
) {
  const result = {
    past: Array<TMeeting>(),
    ongoing: Array<TMeeting>(),
    aboutToStart: Array<TMeeting>(),
    future: Array<TMeeting>(),
  };

  for (const m of meetings) {
    if (meetingIsOver(m, time)) result.past.push(m);
    else if (meetingIsOngoing(m, time)) result.ongoing.push(m);
    else if (meetingIsAboutToStart(m, time, aboutToStartMinuteThreshold)) result.aboutToStart.push(m);
    else result.future.push(m);
  }

  return result;
}

function takeFirstThatShareStartTime<TMeeting extends MeetingLike>(meetings: Array<TMeeting>): Array<TMeeting> {
  if (meetings.length === 0) return [];

  const firstStart = meetings[0].startDateTime;
  return meetings.filter((m) => m.startDateTime === firstStart);
}
